import Button from '@shared/components/core/button/CoreButton';
import { StickyFooter } from '@shared/components/core/structure/CommonBodyLayoutStyles';
import CommandPickerDialog from '@shared/components/input/field/CommandPickerDialog';
import UnsavedChangesDialog from '@shared/components/loading/UnsavedChangesDialog';
import DeprecatedHotkeyDialog from '@shared/components/profile/DeprecatedHotkeyDialog';
import { CommandType } from '@shared/constants/CommandValues';
import { getRoute } from '@shared/hooks/getRoute';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useScrollToQueryParam } from '@shared/hooks/useScrollToQueryParam';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { MacroEditor } from '../macro/MacroEditor';

export default function ProfileEditPageContent() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { organizationId } = useParams();
    const location = useLocation();
    const selectRef = useScrollToQueryParam() ?? undefined;
    const hasChanges = useEditProfileStore((state) => state.hasChanges);
    const id = useEditProfileStore((state) => state.id);
    const originatedByProfileId = useEditProfileStore((state) => state.originatedByProfileId);
    const isPreview = useEditProfileStore((state) => state.isPreview);
    const commandKeyMappings = useEditProfileStore((state) => state.commandKeyMappings);
    const [macroToAddCommand, setMacroToAddCommand] = useState<MacroDisplayModel | undefined>();

    const handleSave = async () => {
        const editProfileStore = useEditProfileStore.getState();
        editProfileStore.setOrganizationId(organizationId ?? null);
        const result = await editProfileStore.saveChanges();
        if (result.resultType === 'Ok' && result.data) {
            if (organizationId) {
                navigate(`/org/${result.data.organizationId}/profiles/edit/${result.data.id}`);
            } else {
                navigate(`/profiles/edit/${result.data.id}`);
            }
        }
        return result;
    };

    const handleAddCommandWithType = (macro: MacroDisplayModel | undefined, commandType: CommandType) => {
        setMacroToAddCommand(undefined);
        const editProfileStore = useEditProfileStore.getState();
        editProfileStore.setHasChanges(true);
        if (!macro) return;
        const updatedMacros = editProfileStore.macros.map((m) => {
            if (m.id !== macro.id) return m;
            return { ...m, commands: [...m.commands, { command: '', commandType }] };
        });
        editProfileStore.updateMacros(updatedMacros);
    };

    const saveAndExit = async () => {
        await handleSave();
    };

    if (!commandKeyMappings) return null;
    return (
        <>
            <MacroEditor
                selectRef={selectRef}
                disabled={isPreview}
                addMacroHref={getRoute(`${location.pathname}/controls/new`)}
            />
            <DeprecatedHotkeyDialog />
            <CommandPickerDialog
                open={!!macroToAddCommand}
                onClose={() => setMacroToAddCommand(undefined)}
                onCommandTypeSelected={(commandType: CommandType) =>
                    handleAddCommandWithType(macroToAddCommand, commandType)
                }
            />

            {hasChanges && (
                <UnsavedChangesDialog
                    hasChanges={hasChanges}
                    setHasChanges={useEditProfileStore.getState().setHasChanges}
                    onSave={async () => {
                        await useEditProfileStore.getState().saveChanges();
                    }}
                />
            )}

            <StickyFooter
                contentPosition="center"
                $visuallyHide={!hasChanges || (id !== undefined && originatedByProfileId !== undefined && !hasChanges)}>
                <Button $color="primary" $size="large" startIcon="save" onClick={saveAndExit}>
                    {t('common:saveAndFinish')}
                </Button>
            </StickyFooter>
        </>
    );
}
